import queryString from 'query-string';
import {
  SPOTIFY_CODE,
  SPOTIFY_NEEDS_RECONNECT,
  SPOTIFY_OAUTH_INTENT,
  SPOTIFY_RECONNECT_PROFILE,
  SPOTIFY_RECONNECT_NOTICE,
  SPOTIFY_ACTIVE_PROFILE_ID,
  SPOTIFY_REFRESH_TOKEN,
  SPOTIFY_TOKEN,
  SPOTIFY_TOKEN_EXPIRES_AT,
  SPOTIFY_USER_INFO,
} from '../constants/storage';
import { addItemToStorage, deleteItemFromStorage, getItemFromStorage } from '../utils';

export const SPOTIFY_SCOPES =
  'playlist-modify-private playlist-modify-public playlist-read-private';

export const SPOTIFY_REAUTH_EVENT = 'spotify:reauth-required';
export const SPOTIFY_REAUTH_REQUIRED_CODE = 'SPOTIFY_REAUTH_REQUIRED';
export const SPOTIFY_PROFILE_MISMATCH_CODE = 'SPOTIFY_PROFILE_MISMATCH';
export const SPOTIFY_REFRESH_MISSING_CODE = 'SPOTIFY_REFRESH_TOKEN_MISSING';
export const SPOTIFY_CODE_EXCHANGE_LOCK = 'SPOTIFY_CODE_EXCHANGE_LOCK';

export type SpotifyOAuthMode = 'reconnect' | 'add_profile' | 'first_connect';

export type SpotifyProfileIdentity = {
  profileId?: string;
  spotifyID?: string | null;
  username?: string;
  image?: string;
  isShared?: boolean;
  ownerId?: string;
  needsReconnect?: boolean;
};

export type SpotifyOAuthIntent = {
  mode: SpotifyOAuthMode;
  nonce?: string;
  targetProfileId?: string;
  expectedSpotifyId?: string;
  expectedDisplayName?: string;
  createdAt: number;
};

const INTENT_TTL_MS = 15 * 60 * 1000; // 15 minutes

export class SpotifyReauthRequiredError extends Error {
  code = SPOTIFY_REAUTH_REQUIRED_CODE;
  constructor(message = 'Spotify reauthorization required') {
    super(message);
    this.name = 'SpotifyReauthRequiredError';
  }
}

function safeParseJson<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function readIntentStorage(): string | null {
  try {
    return sessionStorage.getItem(SPOTIFY_OAUTH_INTENT) || localStorage.getItem(SPOTIFY_OAUTH_INTENT);
  } catch {
    return localStorage.getItem(SPOTIFY_OAUTH_INTENT);
  }
}

function writeIntentStorage(value: string): void {
  try {
    sessionStorage.setItem(SPOTIFY_OAUTH_INTENT, value);
  } catch {
    // sessionStorage may be unavailable
  }
  localStorage.setItem(SPOTIFY_OAUTH_INTENT, value);
}

function removeIntentStorage(): void {
  try {
    sessionStorage.removeItem(SPOTIFY_OAUTH_INTENT);
  } catch {
    // ignore
  }
  localStorage.removeItem(SPOTIFY_OAUTH_INTENT);
}

export function getReconnectProfile(): SpotifyProfileIdentity | null {
  return safeParseJson<SpotifyProfileIdentity>(localStorage.getItem(SPOTIFY_RECONNECT_PROFILE));
}

export function persistReconnectProfile(identity: SpotifyProfileIdentity | null): void {
  if (!identity) {
    localStorage.removeItem(SPOTIFY_RECONNECT_PROFILE);
    return;
  }
  localStorage.setItem(SPOTIFY_RECONNECT_PROFILE, JSON.stringify(identity));
  if (identity.profileId) {
    localStorage.setItem(SPOTIFY_ACTIVE_PROFILE_ID, identity.profileId);
  }
}

function getStoredSpotifyUserIdentity(): SpotifyProfileIdentity | null {
  try {
    const user = JSON.parse(localStorage.getItem('spotifyUser') || 'null');
    if (!user) return null;

    return {
      profileId: user.profileId || localStorage.getItem(SPOTIFY_ACTIVE_PROFILE_ID) || undefined,
      spotifyID: user.spotify_user_id || user.spotifyID || null,
      username: user.username,
      image: typeof user.image === 'string' ? user.image : undefined,
      isShared: user.isShared === true,
      ownerId: user.ownerId,
      needsReconnect: user.needsReconnect === true,
    };
  } catch {
    return null;
  }
}

function sameProfileId(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  return String(a) === String(b);
}

function isUsableIdentity(identity?: SpotifyProfileIdentity | null): boolean {
  return Boolean(identity && (identity.profileId || identity.spotifyID || identity.username));
}

/**
 * Active Songplace Spotify profile only.
 * Never mix fields from a stale reconnect snapshot of a *different* profile
 * (that bug made the UI say "Farhan" while reconnect targeted "UM").
 */
export function resolveReconnectIdentity(
  preferred?: SpotifyProfileIdentity | null
): SpotifyProfileIdentity | null {
  const fromUser = getStoredSpotifyUserIdentity();
  const activeId =
    localStorage.getItem(SPOTIFY_ACTIVE_PROFILE_ID) || fromUser?.profileId || undefined;
  const fromReconnect = getReconnectProfile();

  // Snapshot is only valid when it is for the *active* profile.
  const reconnectForActive =
    fromReconnect &&
    (!activeId ||
      !fromReconnect.profileId ||
      sameProfileId(fromReconnect.profileId, activeId))
      ? fromReconnect
      : null;

  // Preferred caller identity is only used when it matches the active profile
  // (or there is no active profile yet).
  const preferredForActive =
    preferred &&
    isUsableIdentity(preferred) &&
    (!activeId || !preferred.profileId || sameProfileId(preferred.profileId, activeId))
      ? preferred
      : null;

  // Atomic pick: one source wins entirely — never field-mix Farhan + UM.
  const source: SpotifyProfileIdentity | null =
    preferredForActive ||
    (fromUser && (!activeId || !fromUser.profileId || sameProfileId(fromUser.profileId, activeId))
      ? fromUser
      : null) ||
    reconnectForActive ||
    (activeId
      ? {
          profileId: activeId,
          username: preferred?.username || fromReconnect?.username || fromUser?.username,
          spotifyID: preferred?.spotifyID ?? fromReconnect?.spotifyID ?? fromUser?.spotifyID ?? null,
          image: preferred?.image || fromReconnect?.image || fromUser?.image,
        }
      : null);

  if (!isUsableIdentity(source)) {
    return null;
  }

  return {
    profileId: source!.profileId || activeId,
    spotifyID: source!.spotifyID ?? null,
    username: source!.username,
    image: source!.image,
    isShared: source!.isShared === true,
    ownerId: source!.ownerId,
  };
}

/**
 * Whether the *currently active* profile needs Spotify reconnect.
 * Global flag alone is not enough when the user switched to a healthy profile
 * while another profile's reconnect snapshot is still sitting in localStorage.
 */
export function activeProfileNeedsReconnect(): boolean {
  if (localStorage.getItem(SPOTIFY_NEEDS_RECONNECT) !== 'true') {
    return false;
  }

  const active = getStoredSpotifyUserIdentity();
  const reconnect = getReconnectProfile();
  const activeId = localStorage.getItem(SPOTIFY_ACTIVE_PROFILE_ID) || active?.profileId;

  // Reconnect snapshot belongs to a different profile than the one in session → not active.
  if (reconnect?.profileId && activeId && !sameProfileId(reconnect.profileId, activeId)) {
    return false;
  }

  // Healthy active session (secrets loaded for this profile) must not stay blocked by a
  // leftover flag from a previous profile. Self-heal the stale global flag.
  const hasSecrets = Boolean(
    getItemFromStorage(SPOTIFY_TOKEN) && getItemFromStorage(SPOTIFY_REFRESH_TOKEN)
  );
  if (
    hasSecrets &&
    localStorage.getItem('hasSpotifyConnection') === 'true' &&
    active &&
    active.needsReconnect !== true &&
    reconnect?.profileId &&
    activeId &&
    !sameProfileId(reconnect.profileId, activeId)
  ) {
    return false;
  }

  return true;
}

/**
 * Clear Spotify secrets for reauth. Keeps non-secret identity for "Reconnect {name}".
 * Pass the exact profile that failed — do not merge with another profile's snapshot.
 */
export function clearSpotifySession(
  identity?: SpotifyProfileIdentity | null,
  reason?: string
): void {
  // Explicit identity for the failed profile wins as a whole object (no cross-profile merge).
  const existingIdentity = isUsableIdentity(identity)
    ? {
        profileId: identity!.profileId,
        spotifyID: identity!.spotifyID ?? null,
        username: identity!.username,
        image: identity!.image,
        isShared: identity!.isShared === true,
        ownerId: identity!.ownerId,
      }
    : resolveReconnectIdentity(null);

  deleteItemFromStorage(SPOTIFY_TOKEN);
  deleteItemFromStorage(SPOTIFY_REFRESH_TOKEN);
  deleteItemFromStorage(SPOTIFY_TOKEN_EXPIRES_AT);
  deleteItemFromStorage(SPOTIFY_CODE);
  deleteItemFromStorage('SPOTIFY_CODE_TIMESTAMP');
  try {
    sessionStorage.removeItem(SPOTIFY_CODE_EXCHANGE_LOCK);
  } catch {
    // ignore
  }

  localStorage.setItem('hasSpotifyConnection', 'false');
  localStorage.setItem(SPOTIFY_NEEDS_RECONNECT, 'true');
  localStorage.removeItem('spotifyBannerDismissed');

  if (existingIdentity) {
    persistReconnectProfile(existingIdentity);
    if (existingIdentity.profileId) {
      localStorage.setItem(SPOTIFY_ACTIVE_PROFILE_ID, String(existingIdentity.profileId));
    }
    localStorage.setItem(
      'spotifyUser',
      JSON.stringify({
        username: existingIdentity.username || '',
        image: existingIdentity.image || 'dummy',
        profileId: existingIdentity.profileId,
        spotifyID: existingIdentity.spotifyID,
        spotify_user_id: existingIdentity.spotifyID,
        isShared: existingIdentity.isShared === true,
        ownerId: existingIdentity.ownerId,
        needsReconnect: true,
      })
    );
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(SPOTIFY_REAUTH_EVENT, {
        detail: { identity: existingIdentity, reason: reason || 'invalid_grant' },
      })
    );
  }
}

/**
 * Wipe all Spotify-related browser state (logout / switch Songplace account).
 * Does not touch Auth0 until the caller logs out.
 */
export function clearAllSpotifyBrowserState(): void {
  deleteItemFromStorage(SPOTIFY_TOKEN);
  deleteItemFromStorage(SPOTIFY_REFRESH_TOKEN);
  deleteItemFromStorage(SPOTIFY_TOKEN_EXPIRES_AT);
  deleteItemFromStorage(SPOTIFY_CODE);
  deleteItemFromStorage('SPOTIFY_CODE_TIMESTAMP');
  deleteItemFromStorage(SPOTIFY_USER_INFO);
  localStorage.removeItem('spotifyUser');
  localStorage.removeItem('hasSpotifyConnection');
  localStorage.removeItem(SPOTIFY_NEEDS_RECONNECT);
  localStorage.removeItem(SPOTIFY_RECONNECT_PROFILE);
  localStorage.removeItem(SPOTIFY_RECONNECT_NOTICE);
  localStorage.removeItem(SPOTIFY_ACTIVE_PROFILE_ID);
  localStorage.removeItem('spotifyBannerDismissed');
  localStorage.removeItem('postSpotifyRedirect');
  removeIntentStorage();
  try {
    sessionStorage.removeItem(SPOTIFY_CODE_EXCHANGE_LOCK);
  } catch {
    // ignore
  }
}

export function markSpotifyConnected(identity?: SpotifyProfileIdentity | null): void {
  localStorage.setItem('hasSpotifyConnection', 'true');
  localStorage.removeItem(SPOTIFY_NEEDS_RECONNECT);
  localStorage.removeItem('spotifyBannerDismissed');
  localStorage.removeItem(SPOTIFY_RECONNECT_PROFILE);
  if (identity?.profileId) {
    localStorage.setItem(SPOTIFY_ACTIVE_PROFILE_ID, identity.profileId);
  }
}

export function needsSpotifyReconnect(): boolean {
  // Active profile only — never treat another profile's expiry as the current session's.
  return activeProfileNeedsReconnect();
}

export function setSpotifyReconnectNotice(notice: string): void {
  localStorage.setItem(SPOTIFY_RECONNECT_NOTICE, notice);
}

export function takeSpotifyReconnectNotice(): string | null {
  const notice = localStorage.getItem(SPOTIFY_RECONNECT_NOTICE);
  localStorage.removeItem(SPOTIFY_RECONNECT_NOTICE);
  return notice;
}

/** @deprecated Use SpotifyReconnectModal — kept only as a fallback. */
export function confirmSpotifyReconnect(identity?: SpotifyProfileIdentity | null): boolean {
  const profileName = identity?.username || 'this Spotify profile';
  return window.confirm(
    `Reconnect ${profileName}?\n\n` +
      `Use the Spotify account for ${profileName} in this browser. A different account will not update this profile.\n\n` +
      `Note: Some schedulers may have failed because your Spotify account required reauthorization. After reconnecting your account, please reset any affected schedulers to ensure they run correctly.`
  );
}

export function setOAuthIntent(intent: Omit<SpotifyOAuthIntent, 'createdAt' | 'nonce'> & { nonce?: string }): void {
  const full: SpotifyOAuthIntent = {
    ...intent,
    nonce: intent.nonce || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    createdAt: Date.now(),
  };
  writeIntentStorage(JSON.stringify(full));
}

export function getOAuthIntent(): SpotifyOAuthIntent | null {
  try {
    const raw = readIntentStorage();
    if (!raw) return null;
    const intent = JSON.parse(raw) as SpotifyOAuthIntent;
    if (!intent?.createdAt || Date.now() - intent.createdAt > INTENT_TTL_MS) {
      clearOAuthIntent();
      return null;
    }
    return intent;
  } catch {
    return null;
  }
}

export function clearOAuthIntent(): void {
  removeIntentStorage();
}

/**
 * Whether the OAuth return should use the guarded reconnect path
 * (current/expired profile only — never fall through to Add).
 */
export function shouldUseReconnectFlow(intent?: SpotifyOAuthIntent | null): boolean {
  if (intent?.mode === 'reconnect') return true;
  if (intent?.mode === 'add_profile') return false;
  // Intent lost after OAuth, but browser still marks this session as needs-reconnect.
  return needsSpotifyReconnect();
}

export function buildSpotifyAuthorizeUrl(options?: {
  showDialog?: boolean;
  mode?: SpotifyOAuthMode;
  identity?: SpotifyProfileIdentity | null;
}): string {
  const showDialog = options?.showDialog ?? true;
  const mode = options?.mode || 'first_connect';
  const identity =
    mode === 'reconnect'
      ? resolveReconnectIdentity(options?.identity)
      : options?.identity || getReconnectProfile();

  const nonce = `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

  if (mode === 'reconnect') {
    // Always write reconnect intent, even if identity is partial — backend can resolve
    // single-profile / spotifyID under the current Songplace user.
    setOAuthIntent({
      mode: 'reconnect',
      nonce,
      targetProfileId: identity?.profileId,
      expectedSpotifyId: identity?.spotifyID || undefined,
      expectedDisplayName: identity?.username,
    });
    if (identity) {
      persistReconnectProfile(identity);
    }
  } else if (mode === 'add_profile') {
    setOAuthIntent({ mode: 'add_profile', nonce });
  } else {
    setOAuthIntent({ mode: 'first_connect', nonce });
  }

  return (
    'https://accounts.spotify.com/authorize?' +
    queryString.stringify({
      response_type: 'code',
      client_id: process.env.REACT_APP_CLIENT_ID || '',
      scope: SPOTIFY_SCOPES,
      redirect_uri: process.env.REACT_APP_REDIRECT_URI,
      show_dialog: showDialog,
      state: nonce,
    })
  );
}

export function isSpotifyReauthError(error: unknown): boolean {
  const err = error as any;
  if (!err) return false;
  if (err instanceof SpotifyReauthRequiredError) return true;
  if (err?.code === SPOTIFY_REAUTH_REQUIRED_CODE) return true;

  const data = err?.response?.data;
  if (!data) return false;
  if (data.code === SPOTIFY_REAUTH_REQUIRED_CODE) return true;
  if (data.error === 'invalid_grant') return true;
  if (data.error?.error === 'invalid_grant') return true;
  return false;
}

export function getReconnectDisplayName(): string {
  const identity = resolveReconnectIdentity();
  if (identity?.username) return identity.username;
  return 'Spotify';
}

/** Store tokens after successful OAuth / refresh */
export function storeSpotifyTokens(accessToken: string, refreshToken?: string, expiresIn?: number) {
  addItemToStorage(SPOTIFY_TOKEN, accessToken);
  if (refreshToken) {
    addItemToStorage(SPOTIFY_REFRESH_TOKEN, refreshToken);
  }
  if (expiresIn) {
    const expiresAt = Date.now() + expiresIn * 1000;
    addItemToStorage(SPOTIFY_TOKEN_EXPIRES_AT, expiresAt.toString());
  }
}

export function hasValidSpotifySession(): boolean {
  if (needsSpotifyReconnect()) return false;
  const token = getItemFromStorage(SPOTIFY_TOKEN);
  const refresh = getItemFromStorage(SPOTIFY_REFRESH_TOKEN);
  return Boolean(token && refresh);
}

export const MISSING_REFRESH_USER_MESSAGE =
  'Spotify authorized the account but did not issue a new long-lived connection token. Open https://www.spotify.com/account/apps/ , remove access for Songplace, then click Reconnect again.';
